import {
  Attachment,
  AttachmentCreateParams,
  AttachmentList,
  AttachmentsApi,
  ErrorResponse,
  FindAttachmentsRequest,
  SuccessResponse,
} from '@citruscamps/citrus-client'
import { Dispatch, useState } from 'react'
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from 'react-query'
import { DefaultMainPagination, UnlimitedPagination } from '../constants/pagination'
import { Pagination } from '../interfaces/pagination'
import translations from '../translations/en.json'
import { generateApiConfig } from '../utils/client-config'
import { generateBaseKey, generateItemKey, generateListKey } from '../utils/key-generator'
import { useRequestHandler } from './useRequestHandler'
import { useToast } from './useToast'

export const getAttachmentFileId = (
  attachment: Pick<Attachment, 'file'> | Pick<AttachmentCreateParams, 'file_id'>,
): string => ('file_id' in attachment ? attachment.file_id : attachment.file?.id)

export const hasOnlyCreatedAttachments = (
  values?: Array<Attachment | AttachmentCreateParams>,
): values is Attachment[] => !!values?.every((v) => 'id' in v && !!v.id)

export const DefaultAttachmentFetchProps: AttachmentFetchProps = {
  sort: 'created',
  order: 'ASC',
  filter: {},
}

export interface AttachmentFetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  filter?: Omit<FindAttachmentsRequest, 'program_id' | 'limit' | 'skip' | 'order' | 'sort'>
}

interface IProps {
  programId?: string
  pagination?: Pagination
  fetchProps?: AttachmentFetchProps
  overwriteFilters?: boolean
  enabled?: boolean
}

interface FetchAttachments {
  data: Attachment[]
  error?: ErrorResponse
  fetchProps: AttachmentFetchProps
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  setFetchProps: (value?: AttachmentFetchProps) => void
  setPagination: Dispatch<Pagination>
}

export const useFetchAttachments = ({
  programId,
  pagination: initialPagination = DefaultMainPagination,
  fetchProps: initialFetchProps = DefaultAttachmentFetchProps,
  enabled = true,
}: IProps): FetchAttachments => {
  const { requestHandler } = useRequestHandler()
  const queryClient = useQueryClient()
  const [pagination, setPagination] = useState<Pagination>(initialPagination)
  const [fetchProps, setFetchProps] = useState<AttachmentFetchProps>(initialFetchProps)
  const queryKeys = generateListKey({
    type: 'attachment',
    pagination: UnlimitedPagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    program_id: programId,
    query: fetchProps,
  })
  const {
    isLoading,
    isError,
    data: resp,
    error,
  } = useQuery<AttachmentList, ErrorResponse>(
    queryKeys,
    async ({ signal }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new AttachmentsApi(generateApiConfig())
      const skip = pagination.limit ? pagination.page * pagination.limit : undefined
      const response = await requestHandler<AttachmentList>(() =>
        client.findAttachments(
          {
            program_id: programId,
            limit: pagination.limit,
            skip,
            order: fetchProps.order,
            sort: fetchProps.sort,
            file_access:
              fetchProps.filter?.file_access && fetchProps.filter.file_access.length > 0
                ? fetchProps.filter.file_access
                : undefined,
            file_id:
              fetchProps.filter?.file_id && fetchProps.filter.file_id.length > 0
                ? fetchProps.filter.file_id
                : undefined,
            parent_id: fetchProps.filter?.parent_id,
            type:
              fetchProps.filter?.type && fetchProps.filter.type.length > 0
                ? fetchProps.filter.type
                : undefined,
            id:
              fetchProps.filter?.id && fetchProps.filter.id.length > 0
                ? fetchProps.filter.id
                : undefined,
          },
          { signal },
        ),
      )
      response.data.forEach((item) =>
        queryClient.setQueryData(
          generateItemKey({
            type: 'attachment',
            program_id: programId,
            id: item.id,
          }),
          item,
        ),
      )
      return response
    },
    {
      enabled: enabled && !!programId,
    },
  )

  const _setFetchProps = (val?: AttachmentFetchProps) => {
    setPagination(initialPagination)
    return setFetchProps(val || DefaultAttachmentFetchProps)
  }

  return {
    data: resp?.data || [],
    error: error || undefined,
    fetchProps,
    isError,
    isLoading,
    pagination: {
      ...pagination,
      count: resp?.count || pagination.count,
      total: resp?.total || pagination.total,
      page: resp?.page || pagination.page,
      page_count: resp?.page_count || pagination.page_count,
    },
    setFetchProps: _setFetchProps,
    setPagination,
  }
}

interface IInfiniteProps extends IProps {
  fetchAll?: boolean
}

interface FetchInfiniteAttachments extends Omit<FetchAttachments, 'setPagination'> {
  isFetching: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<AttachmentList, ErrorResponse>>
  handleSync: (attachments: Array<Attachment | AttachmentCreateParams>) => Promise<Attachment[]>
  handleCreate: (attachment: AttachmentCreateParams) => Promise<Attachment>
  handleBulkCreate: (attachments: AttachmentCreateParams[]) => Promise<Attachment[]>
  handleDelete: (attachment: Attachment) => Promise<SuccessResponse>
}

export const useFetchInfiniteAttachments = ({
  programId,
  pagination = DefaultMainPagination,
  fetchAll = false,
  enabled = true,
  fetchProps: initialFetchProps = DefaultAttachmentFetchProps,
}: IInfiniteProps): FetchInfiniteAttachments => {
  const { requestHandler } = useRequestHandler()
  const queryClient = useQueryClient()
  const { setToast } = useToast()
  const [fetchProps, setFetchProps] = useState<AttachmentFetchProps>(initialFetchProps)
  const queryKeys = generateListKey({
    type: 'attachment',
    pagination: UnlimitedPagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    program_id: programId,
    query: fetchProps,
  })
  const {
    data: resp,
    error,
    isError,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage = false,
  } = useInfiniteQuery<AttachmentList, ErrorResponse>(
    queryKeys,
    async ({ signal, pageParam: pg }) => {
      pg = pg || pagination
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new AttachmentsApi(generateApiConfig())
      const skip = pg.limit ? pg.page * pg.limit : undefined
      const response = await requestHandler<AttachmentList>(() =>
        client.findAttachments(
          {
            program_id: programId,
            limit: pg.limit,
            skip,
            order: fetchProps.order,
            sort: fetchProps.sort,
            file_access:
              fetchProps.filter?.file_access && fetchProps.filter.file_access.length > 0
                ? fetchProps.filter.file_access
                : undefined,
            file_id:
              fetchProps.filter?.file_id && fetchProps.filter.file_id.length > 0
                ? fetchProps.filter.file_id
                : undefined,
            parent_id: fetchProps.filter?.parent_id,
            type:
              fetchProps.filter?.type && fetchProps.filter.type.length > 0
                ? fetchProps.filter.type
                : undefined,
            id:
              fetchProps.filter?.id && fetchProps.filter.id.length > 0
                ? fetchProps.filter.id
                : undefined,
          },
          { signal },
        ),
      )
      response.data.forEach((item) =>
        queryClient.setQueryData(
          generateItemKey({
            type: 'attachment',
            program_id: programId,
            id: item.id,
          }),
          item,
        ),
      )
      return response
    },
    {
      getNextPageParam: (resp: AttachmentList): Pagination | undefined => {
        if (!resp.page_count || resp.page < resp.page_count - 1) {
          return {
            ...pagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page + 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      getPreviousPageParam: (resp: AttachmentList): Pagination | undefined => {
        if (!resp.page_count || resp.page <= resp.page_count) {
          return {
            ...pagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page - 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      enabled: !!programId && !!enabled,
    },
  )
  const _setFetchProps = (val?: AttachmentFetchProps) => {
    return setFetchProps(val || DefaultAttachmentFetchProps)
  }
  if (!(isFetching || isFetchingNextPage || isLoading) && hasNextPage && fetchAll) {
    fetchNextPage()
  }

  const response = [...(resp?.pages || [])].pop()
  const data = (resp?.pages || []).reduce<Attachment[]>((list, p) => [...list, ...p.data], []) || []

  const handleCreate = async (attachment: AttachmentCreateParams): Promise<Attachment> => {
    if (!programId) {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
      throw new Error('Unable to fetch data')
    }
    const client = new AttachmentsApi(generateApiConfig())
    const response = await requestHandler<Attachment>(() =>
      client.createAttachment({ program_id: programId, AttachmentCreateParams: attachment }),
    )
    await queryClient.invalidateQueries(
      generateBaseKey({
        type: 'attachment',
        program_id: programId,
      }),
    )
    return response
  }

  const handleBulkCreate = async (attachments: AttachmentCreateParams[]): Promise<Attachment[]> => {
    if (!programId) {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
      throw new Error('Unable to fetch data')
    }
    const client = new AttachmentsApi(generateApiConfig())
    const attachmentResult = await Promise.allSettled([
      ...attachments.map((a) =>
        requestHandler<Attachment>(() =>
          client.createAttachment({ program_id: programId, AttachmentCreateParams: a }),
        ),
      ),
    ])
    return attachmentResult
      .filter((r): r is PromiseFulfilledResult<Attachment> => r.status === 'fulfilled')
      .map((r) => r.value)
      .filter((a): a is Attachment => Boolean(a))
  }

  const handleSync = async (
    attachments: Array<Attachment | AttachmentCreateParams>,
  ): Promise<Attachment[]> => {
    if (!fetchAll || !programId) {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
      return []
    }
    const existingAttachments = data?.filter(
      (a) => !!attachments.find((b) => 'id' in b && b.id === a.id),
    )
    const createAttachments: AttachmentCreateParams[] = attachments.filter(
      (a): a is AttachmentCreateParams => !('id' in a),
    )
    const deleteAttachments = data?.filter(
      (a) => !attachments.find((b) => 'id' in b && b.id === a.id),
    )
    const client = new AttachmentsApi(generateApiConfig())
    const attachmentResult = await Promise.allSettled([
      ...createAttachments.map((a) =>
        requestHandler<Attachment>(() =>
          client.createAttachment({ program_id: programId, AttachmentCreateParams: a }),
        ),
      ),
      ...deleteAttachments.map((a) =>
        requestHandler<SuccessResponse>(() =>
          client.deleteAttachment({ program_id: programId, id: a.id }),
        ),
      ),
    ])
    await queryClient.invalidateQueries(
      generateBaseKey({
        type: 'attachment',
        program_id: programId,
      }),
    )
    return [
      ...existingAttachments,
      ...attachmentResult
        .filter(
          (r): r is PromiseFulfilledResult<Attachment> =>
            r.status === 'fulfilled' && 'id' in r.value,
        )
        .map((r) => r.value),
    ]
  }

  const handleDelete = async (attachment: Attachment): Promise<SuccessResponse> => {
    if (!programId) {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
      throw new Error('Unable to fetch data')
    }
    const client = new AttachmentsApi(generateApiConfig())
    const response = await requestHandler<SuccessResponse>(() =>
      client.deleteAttachment({ program_id: programId, id: attachment.id }),
    )
    await queryClient.invalidateQueries(
      generateBaseKey({
        type: 'attachment',
        program_id: programId,
      }),
    )
    return response
  }

  return {
    data,
    error: error || undefined,
    fetchProps,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    pagination: {
      ...pagination,
      count: response?.count || pagination.count,
      total: response?.total || pagination.total,
      page: response?.page || pagination.page,
      page_count: response?.page_count || pagination.page_count,
    },
    fetchNextPage,
    handleSync,
    handleCreate,
    handleBulkCreate,
    handleDelete,
    setFetchProps: _setFetchProps,
  }
}
